import type { ElementType, ReactNode } from "react";
import { forwardRef } from "react";

import classNames from "classnames";

import styles from "./Typography.module.scss";

export type Variant = "h1" | "h2" | "h3" | "h4" | "h5" | "body1" | "body2";

export type Weight = "light" | "regular" | "medium" | "bold";

const variantMapping: Record<Variant, keyof JSX.IntrinsicElements> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  body1: "p",
  body2: "p",
};

export type TypographyProps<T extends ElementType> = {
  as?: T;
  variant: Variant;
  weight?: Weight;
  id?: string;
  dataTestId?: string;
  className?: string;
  children: ReactNode;
};

export const Typography = forwardRef(
  <T extends ElementType = "span">(
    { as, variant, className, dataTestId, children, id, weight = "regular" }: TypographyProps<T>,
    ref: React.Ref<T extends keyof JSX.IntrinsicElements ? JSX.IntrinsicElements[T] : T>
  ) => {
    const Component = as || (variant && variantMapping[variant]) || "span";

    return (
      <Component
        ref={ref}
        id={id}
        className={classNames(styles[`variant-${variant}`], styles[`font-weight-${weight}`], className)}
        data-testid={dataTestId}
      >
        {children}
      </Component>
    );
  }
);
